import { Alert, AlertTitle, Box, Button, Paper, Typography } from '@mui/material'
import { Form, Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useTheme } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import BookingStatus from './components/BookingStatus';
import BookingInformation from './components/BookingInformation';
import Loader from '../../../components/Loader';
import * as Yup from "yup";
import { useDispatch, useSelector } from 'react-redux';
import { addManageRefreshAddUpdate,  getMangeDataById, updateManageByIdRefresh } from '../../../actions/mangeAction';
// removed unused resetData import
import { showSuccessToast } from '../../../slices/toastSlice';
import { useSearchParams } from 'react-router-dom';
import { resetData } from '../../../slices/manageSlice';

function AddNewBooking() {

  const theme = useTheme();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const objectById = useSelector((state) => state.manage.objectById);

  const [initialValues, setInitialValues] = useState(null);


  const dispatch = useDispatch();

  const { updated, added, hasError, errorMessage, loading } = useSelector((state) => state.manage) || {};

  const bookingId = searchParams.get("id") || null;


  const today = new Date();
  const todayStr = today.toISOString().slice(0,10);
  const dateRegex = /^(19|20)\d{2}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
  const parseDate = (v) => (v && dateRegex.test(v) ? new Date(v + 'T00:00:00') : null);

  const validationSchema = Yup.object().shape({
    bookingStatus: Yup.object().required(t('Booking status is required')),
    entryDate: Yup.string()
      .matches(dateRegex, t('Entry date must be in the format YYYY-MM-DD'))
      .required(t('Entry date is required')),




    number: Yup.string().required(t('Booking number is required')),
    destination: Yup.object().required(t('Destination is required')),
    portLoading: Yup.object().required(t('Port loading is required')),
    shippingLine: Yup.object().required(t('Shipping line is required')),
    vesselName: Yup.string().required(t('Vessel name is required')),
    voyageNumber: Yup.string().required(t('Voyage number is required')),
    pickupTerminal: Yup.object().required(t('Pickup terminal is required')),
    returnTerminal: Yup.object().required(t('Return terminal is required')),
    estimatedSailingDate: Yup.string()
      .matches(dateRegex, t('Estimated sailing date must be in the format YYYY-MM-DD'))
      .required(t('Estimated sailing date is required'))
      .test('sailing-not-past', t('Estimated sailing date must be today or in the future'), function(value){
        const d = parseDate(value); if(!d) return true; return d >= parseDate(todayStr); }),



    estimatedArrivalDate: Yup.string()
      .matches(dateRegex, t('Estimated arrival date must be in the format YYYY-MM-DD'))
      .required(t('Estimated arrival date is required'))
      .test('arrival-after-sailing', t('Estimated arrival date must be after estimated sailing date'), function(value){
        const arrival = parseDate(value); if(!arrival) return true; const sailing = parseDate(this.parent.estimatedSailingDate); if(!sailing) return true; return arrival > sailing; }),
    earliestReceivingDate: Yup.string()
      .matches(dateRegex, t('Earliest receiving date must be in the format YYYY-MM-DD'))
      .required(t('Earliest receiving date is required'))
      .test('earliest-not-past', t('Earliest receiving date must be today or in the future'), function(value){
        
        const d = parseDate(value); 
        
        if(!d) return true;


        if(bookingId === null){
          return d >= parseDate(todayStr);
        }
        

        return true;
      
      }
    
    )




      .test('earliest-before-sailing', t('Earliest receiving date must be before or equal estimated sailing date'), function(value){
        const earliest = parseDate(value); if(!earliest) return true; const sailing = parseDate(this.parent.estimatedSailingDate); if(!sailing) return true; return earliest <= sailing; })
      .test('earliest-before-arrival', t('Earliest receiving date must be before estimated arrival date'), function(value){
        const earliest = parseDate(value); if(!earliest) return true; const arrival = parseDate(this.parent.estimatedArrivalDate); if(!arrival) return true; return earliest < arrival; }),
    cutoffDate: Yup.string()
      .matches(dateRegex, t('Cutoff date must be in the format YYYY-MM-DD'))
      .required(t('Cutoff date is required'))
      .test('cutoff-after-earliest', t('Cutoff date must be on/after earliest receiving date'), function(value){
        const cutoff = parseDate(value); if(!cutoff) return true; const earliest = parseDate(this.parent.earliestReceivingDate); if(!earliest) return true; return cutoff >= earliest; })
      .test('cutoff-before-latest', t('Cutoff date must be before latest return date'), function(value){
        const cutoff = parseDate(value); if(!cutoff) return true; const latest = parseDate(this.parent.latestReturnDate); if(!latest) return true; return cutoff < latest; }),
    latestReturnDate: Yup.string()
      .matches(dateRegex, t('Latest return date must be in the format YYYY-MM-DD'))
      .required(t('Latest return date is required'))
      .test('latest-not-past', t('Latest return date must be today or in the future'), function(value){
        const d = parseDate(value); if(!d) return true; return d >= parseDate(todayStr); })
      .test('latest-after-earliest', t('Latest return date must be after earliest receiving date'), function(value){
        const latest = parseDate(value); if(!latest) return true; const earliest = parseDate(this.parent.earliestReceivingDate); if(!earliest) return true; return latest > earliest; })
      .test('latest-before-sailing', t('Latest return date must be before estimated sailing date'), function(value){
        const latest = parseDate(value); if(!latest) return true; const sailing = parseDate(this.parent.estimatedSailingDate); if(!sailing) return true; return latest < sailing; })
      .test('latest-before-arrival', t('Latest return date must be before estimated arrival date'), function(value){
        const latest = parseDate(value); if(!latest) return true; const arrival = parseDate(this.parent.estimatedArrivalDate); if(!arrival) return true; return latest < arrival; }),
  });


  useEffect(() => {
      return () => {
        dispatch(resetData());
      };
  }, [dispatch]);


  const defaultInitialValues = {
    bookingStatus: null,
    entryDate: new Date().toISOString().substr(0, 10), // required today's date
    number: '',
    destination: null,
    portLoading: null,
    shippingLine: null,
    broker: null,
    vesselName: null,
    voyageNumber: null,
    pickupTerminal: null,
    returnTerminal: null,
    estimatedSailingDate: null,
    estimatedArrivalDate: null,
    earliestReceivingDate: null,
    cutoffDate: null,
    latestReturnDate: null,
    note: '',
  };

  const hasFormErrors = (formik) => {
    return Object.keys(formik.errors).length > 0;
  };
  const handleBack = () => {
    window.history.back();
  }

  const handleKeyDown = (event, submitForm) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submitForm();
    }
  };





  const handleSubmit = async (values) => {

    if (bookingId) {

      dispatch(updateManageByIdRefresh({ mode: "booking", id: bookingId, data: values }));

    } else {

    dispatch(addManageRefreshAddUpdate({ mode: "booking", data: values }));
    }

  };



  useEffect(() => {
    if (bookingId) {

      dispatch(getMangeDataById({ mode: "booking", id: bookingId }));
    }
  }, [dispatch, bookingId]);



  useEffect(() => {


    if (bookingId) {


      if (objectById.loading === "succeeded") {

        setInitialValues({
          ...objectById.content,

        });
      }






    } else {
      setInitialValues(defaultInitialValues)

    }




  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, objectById.loading])




  React.useEffect(() => {
    if (added || updated) {


      if (added) {
        if (added) {
          dispatch(showSuccessToast(`The booking has been saved successfully`));


        }
      }

      if (updated) {
        dispatch(showSuccessToast(`The booking has been updated successfully`));
      }


      window.history.back();
    }
  }, [added, updated, dispatch]);



  if ((bookingId && (objectById.loading === "pending" || objectById.loading === "idle" )) || !initialValues ) {
    return <Loader />;
  }

  return (
    <>
      <Formik onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize
        initialValues={initialValues}>
        {(formik) => (
          <Form>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, mt: { xs: 2, md: 0 } }}>
              <Box sx={{ position: 'relative', width: 48, height: 48 }}>
                <EditCalendarIcon sx={{ color: theme.palette.primary.main, fontSize: 40, position: 'absolute', left: 0, top: 0, zIndex: 1 }} />
                <AddCircleOutlineIcon sx={{ color: theme.palette.secondary.main, fontSize: 24, position: 'absolute', right: -8, bottom: -8, zIndex: 2, bgcolor: theme.palette.background.paper, borderRadius: '50%' }} />
              </Box>
              <Typography variant="h4" sx={{ color: '#0B3C5D', fontWeight: 800, letterSpacing: 1 }}>
                {bookingId ? t('Edit Booking') : t('Add New Booking')}
              </Typography>

            </Box>
            {/*  Status */}
            <BookingStatus />

            <Paper elevation={3} sx={{
              borderRadius: 4,
              boxShadow: '0 4px 24px 0 rgba(11, 60, 93, 0.10)',
              bgcolor: theme.palette.background.paper,
              p: { xs: 2, md: 4 },
              mt: 2,
              maxWidth: 1200,
              mx: 'auto',
            }}>

              <BookingInformation disabled={loading === "pending"} />

            </Paper>



            {hasFormErrors(formik) || hasError ? (
              <Box className="errorBox" sx={{ maxWidth: 700, mx: 'auto', mt: 3 }}>
                <Alert severity="error" sx={{ borderRadius: 2, boxShadow: '0 2px 8px 0 rgba(217, 179, 16, 0.10)' }}>
                  <AlertTitle sx={{ color: theme.palette.warning.dark }}>Error</AlertTitle>
                  {Object.keys(formik.errors).map((fieldName, index) => {
                    if (Array.isArray(formik?.errors[fieldName])) {
                      return null;
                    }
                    return <div key={index}>{formik.errors[fieldName]}</div>;
                  })}
                  {hasError ? <div>{errorMessage}</div> : ""}
                </Alert>
              </Box>
            ) : (
              ""
            )}


            <Box display="flex" flexDirection={{ xs: 'column', md: 'row-reverse' }} alignItems="center" gap={2} margin={{ xs: '32px 0', md: '64px 49px' }}>
              {loading !== "pending" ? (
                <div onKeyDown={(e) => handleKeyDown(e, formik.submitForm)}>
                  <Button
                    variant="contained"
                    type="submit"
                    sx={{
                      bgcolor: theme.palette.secondary.main,
                      color: theme.palette.primary.main,
                      fontWeight: 700,
                      borderRadius: '10px',
                      width: { xs: '100%', md: '231px' },
                      height: '55px',
                      boxShadow: '0 2px 8px 0 rgba(217, 179, 16, 0.10)',
                      fontSize: 18,
                      mb: { xs: 1, md: 0 },
                      '&:hover': { bgcolor: theme.palette.secondary.dark, color: '#fff' },
                    }}
                  >
                    {t("save")}
                  </Button>
                </div>
              ) : (
                <Loader />
              )}
              <Button
                onClick={() => handleBack()}
                variant="outlined"
                sx={{
                  border: `2px solid ${theme.palette.primary.main}`,
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  borderRadius: '10px',
                  width: { xs: '100%', md: '231px' },
                  height: '55px',
                  fontSize: 18,
                  bgcolor: theme.palette.background.paper,
                  '&:hover': { bgcolor: theme.palette.primary.main, color: '#fff', borderColor: theme.palette.secondary.main },
                }}
              >
                {t("cancel")}
              </Button>
            </Box>

          </Form>
        )}
      </Formik>
    </>
  );
}

export default AddNewBooking